import React, { useState } from 'react';
import cn from 'classnames';

import Loader from 'components/Loader';
import classes from './input.module.scss';
import { IInput } from './types';

const Input: React.FC<IInput> = ({ 
	className,
	value,
	onChange,
	type = "text",
	placeholder,
	startIcon = null,
	endIcon = null,
	error,
	renderInput,
	children,
	onClick,
	onFocus,
	onBlur,
	disabled,
	isLoading,
	...restProps
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const isPlaceholderFloat = placeholder && (isFocused || value?.length);
	const inputType = type === 'number' ? 'text' : type;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		let value = e.target.value;
		if (type === 'number') {
			value = value.replace(/[^0-9.]/g, '');
		}
		onChange(value);
	};

	const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
		e.stopPropagation();
		if (onClick) {
			onClick();
		}
	};

	const handleFocus = (): void => {
		setIsFocused(true);
		if (onFocus) {
			onFocus();
		}
	};

	const handleBlur = (): void => {
		setIsFocused(false);
		if (onBlur) {
			onBlur();
		}
	};

	const inputProps = {
		className: cn(classes.inputElement, {
			[classes.inputElement__withStartIcon]: !!startIcon,
			[classes.inputElement__withEndIcon]: !!endIcon || isLoading,
		}),
		value,
		type: inputType,
		onChange: handleChange,
		onFocus: handleFocus,
		onBlur: handleBlur,
		disabled,
		...restProps
	};

	return (
		<div
			className={cn(
				classes.input,
				className,
				{
					[classes.input__error]: error,
					[classes.input__focused]: isFocused,
					[classes.input__disabled]: disabled,
				}
			)}
		>
			<div
				className={cn(classes.inputContainer, { [classes.inputContainer__float]: isPlaceholderFloat || value?.length })}
				onClick={handleClick}
			>
				{startIcon && <div className={classes.startIcon}>{startIcon}</div>}
				{placeholder && (
					<div className={cn(classes.placeholder, { [classes.placeholder__withIcon]: !!startIcon})}>{placeholder}</div>
				)}
				{renderInput
					? renderInput(inputProps)
					: <input {...inputProps} />
				}
				{isLoading && <Loader className={classes.endIcon} itemClassName={classes.loaderItem} />}
				{endIcon && !isLoading && <div className={classes.endIcon}>{endIcon}</div>}
			</div>
			{error && <div className={classes.error}>{error}</div>}
			{children}
		</div>
	);
};

export default Input;
